import { useEffect, useRef, useState } from 'react';

const TWILIO_SDK_URL = 'https://cdn.jsdelivr.net/npm/@twilio/voice-sdk@2.17.0/dist/twilio.min.js';
const TWILIO_SDK_INTEGRITY = 'sha384-z4eYcmT6PHPmQckVQomhfpeYBIY61msHD6UqPnCeIKqHcuW8glx83j8pmTrCDAYO';

interface BrowserCall {
  accept: () => void;
  disconnect: () => void;
  reject: () => void;
  sendDigits: (digits: string) => void;
  on: (event: string, listener: (...args: unknown[]) => void) => void;
}

interface BrowserDevice {
  register: () => Promise<void>;
  destroy: () => void;
  updateToken: (token: string) => void;
  on: (event: string, listener: (...args: unknown[]) => void) => void;
}

interface TwilioGlobal {
  Device: new (token: string, options?: Record<string, unknown>) => BrowserDevice;
}

declare global {
  interface Window { Twilio?: TwilioGlobal; }
}

let sdkPromise: Promise<TwilioGlobal> | null = null;

function loadTwilioSdk(): Promise<TwilioGlobal> {
  if (window.Twilio?.Device) return Promise.resolve(window.Twilio);
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise<TwilioGlobal>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${TWILIO_SDK_URL}"]`);
    const script = existing || document.createElement('script');
    const finish = () => window.Twilio?.Device
      ? resolve(window.Twilio)
      : reject(new Error('Voice SDK не загрузился'));
    script.addEventListener('load', finish, { once: true });
    script.addEventListener('error', () => reject(new Error('Voice SDK недоступен')), { once: true });
    if (!existing) {
      script.src = TWILIO_SDK_URL;
      script.async = true;
      script.crossOrigin = 'anonymous';
      script.integrity = TWILIO_SDK_INTEGRITY;
      document.head.appendChild(script);
    }
  }).catch((error) => {
    sdkPromise = null;
    throw error;
  });
  return sdkPromise;
}

export type BrowserVoiceLineState = 'idle' | 'registering' | 'ready' | 'ringing' | 'connected' | 'ended';

export function useBrowserVoiceLine(
  createAccess: (reservationId: string) => Promise<{ token: string }>,
  onError: (message: string) => void,
  onEnded?: () => void,
) {
  const [lineId, setLineId] = useState<string | null>(null);
  const [lineState, setLineState] = useState<BrowserVoiceLineState>('idle');
  const [incomingCall, setIncomingCall] = useState<BrowserCall | null>(null);
  const deviceRef = useRef<BrowserDevice | null>(null);
  const callRef = useRef<BrowserCall | null>(null);
  const createAccessRef = useRef(createAccess);
  const onErrorRef = useRef(onError);
  const onEndedRef = useRef(onEnded);

  useEffect(() => { createAccessRef.current = createAccess; }, [createAccess]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);
  useEffect(() => { onEndedRef.current = onEnded; }, [onEnded]);

  useEffect(() => () => {
    callRef.current?.disconnect();
    deviceRef.current?.destroy();
    deviceRef.current = null;
  }, []);

  const refreshToken = async (reservationId: string) => {
    try {
      const access = await createAccessRef.current(reservationId);
      deviceRef.current?.updateToken(access.token);
    } catch (error) {
      onErrorRef.current(error instanceof Error ? error.message : 'Не удалось обновить голосовую линию');
    }
  };

  const prepareLine = async (reservationId: string) => {
    setLineId(reservationId);
    setLineState('registering');
    onErrorRef.current('');
    try {
      const [Twilio, access] = await Promise.all([loadTwilioSdk(), createAccessRef.current(reservationId)]);
      deviceRef.current?.destroy();
      const device = new Twilio.Device(access.token, { closeProtection: true });
      deviceRef.current = device;
      device.on('registered', () => setLineState('ready'));
      device.on('incoming', (...args) => {
        const call = args[0] as BrowserCall;
        callRef.current = call;
        setIncomingCall(call);
        setLineState('ringing');
        call.on('accept', () => setLineState('connected'));
        const finish = () => {
          setLineState('ended');
          setIncomingCall(null);
          onEndedRef.current?.();
        };
        call.on('disconnect', finish);
        call.on('cancel', finish);
        call.on('reject', finish);
      });
      device.on('tokenWillExpire', () => { void refreshToken(reservationId); });
      device.on('error', (...args) => {
        const sdkError = args[0] as { message?: string } | undefined;
        onErrorRef.current(sdkError?.message || 'Не удалось подготовить линию');
        setLineState('idle');
      });
      await device.register();
    } catch (error) {
      onErrorRef.current(error instanceof Error ? error.message : 'Не удалось подготовить голосовую линию');
      setLineState('idle');
    }
  };

  const acceptCall = () => {
    incomingCall?.accept();
    setLineState('connected');
  };

  const finishCall = () => {
    if (lineState === 'ringing') incomingCall?.reject();
    else callRef.current?.disconnect();
    setLineState('ended');
    setIncomingCall(null);
  };

  const sendDigit = (digit: string) => callRef.current?.sendDigits(digit);

  return { lineId, lineState, prepareLine, acceptCall, finishCall, sendDigit };
}
